import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import './Chat.css';

const Chat = ({ username, roomId }) => {
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [roomUsers, setRoomUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Initialize socket connection
  useEffect(() => {
    if (!username || !roomId) return;

  const newSocket = io(process.env.REACT_APP_CHATSERVER, {
       transports: ["polling", "websocket"], // 🔑 MOST IMPORTANT
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  timeout: 20000,
    withCredentials: true,   // ← yahi credentials

    });
    
    setSocket(newSocket);

    // Socket event listeners
    newSocket.on('connect', () => {
      console.log('Connected to chat server');
      setIsConnected(true);
      
      // Join room after connection
      newSocket.emit('joinRoom', { username, roomId });
    });

    newSocket.on('disconnect', () => {
      console.log('Disconnected from chat server');
      setIsConnected(false);
    });

    newSocket.on('past-messages', (pastMessages) => {
      setMessages(pastMessages);
    });

    newSocket.on('receive-message', (newMessage) => {
      setMessages(prev => [...prev, newMessage]);
    });

    newSocket.on('system-message', (systemMsg) => {
      setMessages(prev => [...prev, {
        ...systemMsg,
        type: 'system',
        username: 'System'
      }]);
    });

    newSocket.on('user-joined', (joinedUser) => {
      if (joinedUser !== username) {
        setMessages(prev => [...prev, {
          type: 'system',
          username: 'System',
          message: `${joinedUser} joined the chat`,
          timestamp: new Date().toISOString()
        }]);
      }
    });

    newSocket.on('user-left', (leftUser) => {
      if (leftUser !== username) {
        setMessages(prev => [...prev, {
          type: 'system',
          username: 'System',
          message: `${leftUser} left the chat`,
          timestamp: new Date().toISOString()
        }]);
      }
    });

    newSocket.on('room-users', (users) => {
      setRoomUsers(users);
    });

    newSocket.on('typing', (typingUser) => {
      if (typingUser !== username) {
        setTypingUsers(prev => {
          if (!prev.includes(typingUser)) {
            return [...prev, typingUser];
          }
          return prev;
        });
      }
    });

    newSocket.on('stopTyping', (stoppedTypingUser) => {
      setTypingUsers(prev => prev.filter(user => user !== stoppedTypingUser));
    });

    newSocket.on('error', (error) => {
      console.error('Chat socket error:', error);
    });

    // Clean up on unmount
    return () => {
      if (newSocket) {
        newSocket.disconnect();
      }
    };
  }, [username, roomId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle typing indicator
  useEffect(() => {
    if (socket && isTyping) {
      socket.emit('typing');
    } else if (socket && !isTyping) {
      socket.emit('stopTyping');
    }
  }, [isTyping, socket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTyping = () => {
    if (!isTyping) {
      setIsTyping(true);
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!message.trim() || !socket || !isConnected) return;

    socket.emit('send-message', {
      username,
      message: message.trim(),
      roomId
    });

    setMessage('');
    setIsTyping(false);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const getTypingText = () => {
    if (typingUsers.length === 0) return null;
    
    if (typingUsers.length === 1) {
      return `${typingUsers[0]} is typing...`;
    }
    
    if (typingUsers.length === 2) {
      return `${typingUsers[0]} and ${typingUsers[1]} are typing...`;
    }
    
    return `${typingUsers[0]} and ${typingUsers.length - 1} others are typing...`;
  };

  if (!username || !roomId) {
    return (
      <div className="chat-container">
        <div className="chat-placeholder">
          <p>Chat is not available. Missing username or room ID.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="room-info">
          <h4>Chat Room: {roomId}</h4>
          <p>Connected as: <strong>{username}</strong></p>
        </div>
        <div className="connection-status">
          <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`} />
          <span>{isConnected ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      {/* Users Panel */}
      <div className="users-panel">
        <div className="users-header">
          <h5>Online Users ({roomUsers.length})</h5>
        </div>
        <div className="users-list">
          {roomUsers.map((user, index) => (
            <div 
              key={index} 
              className={`user-item ${user === username ? 'current-user' : ''}`}
            >
              <span className="user-icon">👤</span>
              <span className="user-name">
                {user === username ? `${user} (You)` : user}
                {typingUsers.includes(user) && user !== username && (
                  <span className="typing-indicator-small">...</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Messages Container */}
      <div className="messages-container">
        {messages.map((msg, index) => (
          <div 
            key={index} 
            className={`message ${msg.type === 'system' ? 'system' : ''} ${
              msg.username === username ? 'current-user' : ''
            }`}
          >
            {msg.type === 'system' ? (
              <div className="system-message">
                <span className="system-text">{msg.message}</span>
                <span className="message-time">
                  {formatTime(msg.timestamp)}
                </span>
              </div>
            ) : (
              <>
                <div className="message-header">
                  <span className="message-username">
                    {msg.username}
                  </span>
                  <span className="message-time">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
                <div className="message-content">
                  {msg.message}
                </div>
              </>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing Indicator */}
      {typingUsers.length > 0 && (
        <div className="typing-indicator">
          <span className="typing-dots">
            <span>.</span><span>.</span><span>.</span>
          </span>
          <span className="typing-text">{getTypingText()}</span>
        </div>
      )}

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="message-input-container">
        <textarea
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            handleTyping();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e);
            }
          }}
          onBlur={() => setIsTyping(false)}
          placeholder="Type your message here..."
          rows={2}
          maxLength={500}
          className="message-input"
        />
        <button 
          type="submit" 
          className="send-button"
          disabled={!message.trim() || !isConnected}
        >
          Send
        </button>
      </form>
    </div>
  );
};

export default Chat;